import taskService from '@/domain/services/taskService';
import workIntervalService from '@/domain/services/workIntervalService';
import authService from '@/data/api/authService';
import syncApi from '@/data/api/sync';
import usersApi from '@/data/api/users';
import { createWebLocalDatabase } from '@/data/web-db/dexieLocalDatabase';
import { createTaskLocalReadModel } from '@/data/web-db/taskLocalReadModel';
import { createWorkIntervalLocalReadModel } from '@/data/web-db/workIntervalLocalReadModel';
import { createWebLocalSyncCoordinator } from '@/domain/services/sync/webLocalSyncCoordinator';
import { createBrowserPerformanceTelemetry } from '@/i12e/performance/browserPerformanceTelemetry';
import { createNoopPerformanceTelemetry } from '@/app/telemetry/performanceTelemetry';
import { createMeasuredSyncCoordinator } from '@/app/telemetry/createMeasuredSyncCoordinator';

/**
 * Wires application services to their concrete data adapters.
 *
 * Keeping this assembly independent from Vue makes the composition boundary
 * reusable for tests and future non-browser entrypoints.
 */
export const configureDependencies = () => {
  // Profiling is opt-in and development-only so normal local loads stay quiet.
  const performanceTelemetryEnabled =
    import.meta.env.MODE === 'development' &&
    import.meta.env.VITE_ENABLE_PERFORMANCE_TELEMETRY === 'true';
  const performanceTelemetry = performanceTelemetryEnabled
    ? createBrowserPerformanceTelemetry({ enabled: true })
    : createNoopPerformanceTelemetry();
  const database = createWebLocalDatabase();
  const syncCoordinator = createWebLocalSyncCoordinator({
    database,
    pullApi: syncApi,
    identity: usersApi
  });
  const measuredSyncCoordinator = createMeasuredSyncCoordinator(
    syncCoordinator,
    performanceTelemetry
  );

  // Any session boundary must clear the authenticated database context before
  // another user can initialize a local read, preventing cross-user reuse.
  const resetSyncContext = () => {
    syncCoordinator.reset();
  };
  authService.addEventListener('auth:logout', resetSyncContext);
  authService.addEventListener('auth:session-changed', resetSyncContext);

  // Both entity read models use the same database, identity, cursor and pull.
  taskService.setTaskLocalReadModel(
    createTaskLocalReadModel({
      database,
      syncCoordinator: measuredSyncCoordinator
    })
  );
  workIntervalService.setWorkIntervalLocalReadModel(
    createWorkIntervalLocalReadModel({
      database,
      syncCoordinator: measuredSyncCoordinator
    })
  );

  return { performanceTelemetry };
};
